import React, { useEffect, useState } from "react";
import { X, ClipboardList, Globe } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import ApiService from "../../services/Api.service";

const VoiceCharacter = ({
  isOpen,
  onClose,
  onSave,
  itemData,
}) => {
  const [languageMode, setLanguageMode] = useState("default");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [languageOptions, setLanguageOptions] = useState({});
  const [ttsOptions, setTtsOptions] = useState([]);
  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      tts_type: "",
      voice_id: "",
      speech_text: "नमस्ते! मैं आपका virtual assistant बोल रहा हूँ / रही हूँ । मैं आपकी मदद के लिए यहाँ हूँ। Please बताइए, मैं कैसे help कर सकता हूँ / सकती हूँ?",
      language_mode: "default",
      language: "",
      gender: "female",
      is_active: true,
    },
  });

  const fetchLanguageOptions = async () => {
    try {
      const languageRes = await ApiService.getLanguageOptions();
      if (languageRes.status === 200) {
        setLanguageOptions(languageRes.data);

        const defaultLang =
          Array.isArray(languageRes.data?.default_language) && languageRes.data.default_language.length > 0
            ? languageRes.data.default_language[0].value
            : "";
        setSelectedLanguage(defaultLang);
      }
    } catch (error) {
      console.error("Error fetching language options:", error);
    }
  };

  const fetchTtsOptions = async (selectedLanguage) => {
    try {
      const payload = {
        language: selectedLanguage
      }
      const ivrOptionsRes = await ApiService.getIvrOptions(payload);

      if (ivrOptionsRes.status === 200) {
        setTtsOptions(ivrOptionsRes.data.tts_options || []);
      }
    } catch (error) {
      console.error("Error fetching TTS options:", error);
    }
  };

  useEffect(() => {
    fetchLanguageOptions();
  }, []);

  useEffect(() => {
    if (selectedLanguage) {
      fetchTtsOptions(selectedLanguage);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (itemData) {
      reset({
        name: itemData.name || "",
        description: itemData.description || "",
        tts_type: itemData.tts_type || "",
        voice_id: itemData.voice_id || "",
        speech_text: itemData.speech_text || "नमस्ते! मैं आपका virtual assistant बोल रहा हूँ / रही हूँ । मैं आपकी मदद के लिए यहाँ हूँ। Please बताइए, मैं कैसे help कर सकता हूँ / सकती हूँ?",
        language:
          itemData.language && itemData.language !== languageOptions.default_language?.[0]?.value
            ? itemData.language
            : languageOptions.default_language?.[0]?.value || "",
        language_mode:
          itemData.language && itemData.language !== languageOptions.default_language?.[0]?.value
            ? "custom"
            : "default",
        gender: itemData.gender || "female",
        is_active: itemData.is_active !== false,
      });
    } else {
      reset({
        name: "",
        description: "",
        tts_type: "",
        voice_id: "",
        speech_text: "नमस्ते! मैं आपका virtual assistant बोल रहा हूँ / रही हूँ । मैं आपकी मदद के लिए यहाँ हूँ। Please बताइए, मैं कैसे help कर सकता हूँ / सकती हूँ?",
        language_mode: "default",
        language: languageOptions.default_language?.[0]?.value || "",
        gender: "female",
        is_active: true,
      });
    }
  }, [itemData, reset, languageOptions]);

  const onSubmit = (data) => {
    const payload = {
      name: data.name.trim(),
      description: data.description.trim(),
      tts_type: data.tts_type,
      voice_id: data.voice_id.trim(),
      speech_text: data.speech_text.trim(),
      language:
        data.language_mode === "default"
          ? languageOptions?.default_language?.[0]?.value
          : data.language,
      gender: data.gender,
      is_active: data.is_active,
    };

    onSave(payload);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="add-subparam-overlay">
      <div
        className="add-subparam-modal"
        style={{ maxWidth: 650, width: "90%" }}
      >
        {/* Header */}
        <div className="add-subparam-header">
          <h3>
            <ClipboardList size={18} />
            {itemData ? "Edit Voice Character" : "Add Voice Character"}
          </h3>

          <button onClick={onClose} className="close-btn">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form
          id="voiceCharacterForm"
          onSubmit={handleSubmit(onSubmit)}
          className="add-subparam-body"
        >
          {/* Row 1 */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label>Name *</label>
              <Controller
                name="name"
                control={control}
                rules={{ required: "Name is required" }}
                render={({ field }) => (
                  <input
                    {...field}
                    className="form-control"
                    placeholder="Voice Character Name"
                  />
                )}
              />
              {errors.name && (
                <p className="error-text">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label>Voice ID *</label>
              <Controller
                name="voice_id"
                control={control}
                rules={{ required: "Voice ID is required" }}
                render={({ field }) => (
                  <input
                    {...field}
                    className="form-control"
                    placeholder="hi-IN-SwaraNeural"
                  />
                )}
              />
              {errors.voice_id && (
                <p className="error-text">{errors.voice_id.message}</p>
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginTop: 10,
            }}
          >
            <div>
              <label>Gender</label>
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <select {...field} className="form-control">
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                )}
              />
            </div>
          </div>

          {/* Language */}
          <div style={{ marginTop: 10 }}>
            <label>
              <Globe size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />
              Language Mode
            </label>

            <Controller
              name="language_mode"
              control={control}
              render={({ field }) => (
                <div className="flex gap-3" style={{ display: "flex", gap: "20px" }}>
                  {/* DEFAULT LANGUAGE */}
                  <label className="radio-label">
                    <input
                      type="radio"
                      value="default"
                      checked={field.value === "default"}
                      onChange={() => {
                        field.onChange("default");
                        setLanguageMode("default");
                        setSelectedLanguage(languageOptions.default_language?.[0]?.value || "");
                      }}
                    />
                    {languageOptions.default_language?.[0]?.label || "English"} (Default)
                  </label>

                  {/* CUSTOM LANGUAGE */}
                  <label className="radio-label">
                    <input
                      type="radio"
                      value="custom"
                      checked={field.value === "custom"}
                      onChange={() => field.onChange("custom")}
                    />
                    Select Other Language
                  </label>
                </div>
              )}
            />

            {/* SHOW DROPDOWN ONLY IF custom selected */}
            {watch("language_mode") === "custom" && (
              <Controller
                name="language"
                control={control}
                rules={{ required: "Please select language" }}
                render={({ field }) => (
                  <select
                    {...field}
                    className="form-control"
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setSelectedLanguage(e.target.value);
                    }}
                  >
                    <option value="">Select Language</option>
                    {languageOptions.other_languages?.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                )}
              />
            )}
          </div>

          {/* TTS Type */}
          <div style={{ marginTop: 10 }}>
            <label>TTS Type *</label>
            <Controller
              name="tts_type"
              control={control}
              render={({ field }) => (
                <select {...field} className="form-control">
                  <option value="">Select TTS</option>
                  {ttsOptions?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>

          {/* Description */}
          <div style={{ marginTop: 10 }}>
            <label>Description</label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  rows={2}
                  className="form-control"
                  placeholder="Voice Description"
                />
              )}
            />
          </div>

          {/* Preview Text */}
          <div style={{ marginTop: 10 }}>
            <label>Speech text *</label>
            <Controller
              name="speech_text"
              control={control}
              rules={{ required: "Speech text is required" }}
              render={({ field }) => (
                <textarea
                  {...field}
                  rows={3}
                  className="form-control"
                />
              )}
            />
            {errors.speech_text && (
              <p className="error-text">{errors.speech_text.message}</p>
            )}
          </div>

          {/* Active */}
          <div
            style={{
              marginTop: 10,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Controller
              name="is_active"
              control={control}
              render={({ field }) => (
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={(e) =>
                    field.onChange(e.target.checked)
                  }
                />
              )}
            />
            <label style={{ margin: 0 }}>Active</label>
          </div>
        </form>

        {/* Footer */}
        <div className="add-subparam-footer">
          <button
            type="button"
            className="popup-cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            type="submit"
            form="voiceCharacterForm"
            className="popup-add-btn"
          >
            {itemData ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceCharacter;